import { useMemo, useState } from "react";
import moment from "moment";
import { useAppTranslation } from "../../../../../../../../i18n/useAppTranslation";
import { uiNotification } from "../../../../../../../../services/UINotificationService";
import { useSalesExpiredLiabilityReportQuery } from "../../../../../../../../api/queries/useSalesExpiredLiabilityReportQuery";
import { useClinicsQuery } from "../../../../../../../../api/queries/useClinicsQuery";

const prepareReqPeriod = (periodState) => {
  return {
    from: periodState.from || null,
    to: periodState.to || null,
  };
};

export function useReports() {
  const { tCommon } = useAppTranslation.Common();

  const [period, setPeriod] = useState({
    from: moment().startOf("month").toDate(),
    to: moment().endOf("month").toDate(),
  });

  const [clinicId, setClinicId] = useState(null);

  const { data: clinics, isLoading: isClinicsLoading } = useClinicsQuery();

  const {
    data,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useSalesExpiredLiabilityReportQuery({
    payload: {
      period: prepareReqPeriod(period),
      pageSize: 15,
      clinicId: clinicId,
    },
    options: {
      staleTime: 0,
      cacheTime: 0,
      onError: () => {
        uiNotification.error(tCommon("error.getReports"));
      },
    },
  });

  const isPeriodActive = Object.values(period).some(Boolean);

  const isSoftLoading =
    isPeriodActive &&
    isFetching &&
    !isLoading &&
    !isFetchingNextPage &&
    !isClinicsLoading;

  const reports = useMemo(() => {
    if (data?.pages) {
      return data.pages
        .map((p) => p.reports)
        .reduce((r, p) => [...r, ...p], []);
    }
    return [];
  }, [data?.pages]);

  const allClinics = useMemo(() => {
    const clinicsOptions = clinics?.map((clinic) => ({
      value: clinic.id,
      label: clinic.name,
    }));
    return clinicsOptions || [];
  }, [clinics]);

  return {
    reports: {
      total: data?.pages?.[0]?.stats?.total,
      value: reports,
      isLoading,
      isSoftLoading,
      hasMore: Boolean(hasNextPage),
      fetchMore: fetchNextPage,
      isNextFetching: isFetchingNextPage,
    },
    period: {
      value: period,
      update: setPeriod,
    },
    clinic: {
      value: clinicId,
      onChange: setClinicId,
      options: allClinics,
    },
  };
}
